import { motion } from 'framer-motion';
import { Users, Heart, Zap, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '@/components/MetaTags';

const Eyebrow = ({ children, light }) => (
  <span className="flex items-center gap-3 font-mono uppercase text-xs tracking-[3px]" style={{ color: light ? 'rgba(255,255,255,0.7)' : 'rgba(8,0,16,0.6)' }}>
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    {children}
  </span>
);

const AboutPage = () => {
  const values = [
    { icon: Users, title: 'Community First', desc: 'We bring people together. Every crawl is a chance to make new friends and unforgettable memories.', color: '#FF2D78' },
    { icon: Heart, title: 'Authentic Experiences', desc: "No tourist traps. We show you the real spots that locals love - the hidden gems you'd never find on your own.", color: '#00F5FF' },
    { icon: Zap, title: 'Epic Energy', desc: 'We bring the hype. Our crawls are high-energy, well-organized experiences that keep the party going all night.', color: '#B8FF2E' },
    { icon: Globe, title: 'Nationwide Reach', desc: "From coast to coast, we're building the largest bar crawl community in America.", color: '#FF2D78' },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      <MetaTags
        title="About CBC — Our Story"
        description="Crawl Baby Crawl started with one idea: bar crawls should actually be fun. Now we're the fastest-growing crawl company in America."
        path="/about"
      />
      {/* Hero — Electric Lime */}
      <section className="relative py-20 pb-32 md:pb-20 px-4 overflow-hidden hero-diagonal-clip" style={{ backgroundColor: '#B8FF2E' }} data-testid="about-hero">
        {/* Mobile-only photo bg + tint */}
        <div className="absolute inset-0 md:hidden">
          <img src="https://customer-assets.emergentagent.com/job_50c259f3-df0a-4de3-8458-52ce4eb888c4/artifacts/o9tfza6d_dd1fe97813dbac2a75a4ae8ad4821858e0-2-music-festival.rsocial.w1200.jpg.webp" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(184,255,46,0.60)' }} />
        </div>
        {/* Mobile-only dark text scrim */}
        <div className="absolute inset-0 md:hidden pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(8,0,16,0.45) 0%, rgba(8,0,16,0.2) 50%, transparent 80%)' }} />
        {/* CBC watermark */}
        <div className="absolute right-[-40px] top-1/2 -translate-y-1/2 pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[140px] md:text-[280px] lg:text-[360px] leading-none uppercase" style={{ color: 'rgba(8,0,16,0.08)' }}>CBC</span>
        </div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="flex items-center gap-3 font-mono uppercase text-xs tracking-[3px] text-white/80 md:text-[rgba(8,0,16,0.6)]">
              <span className="inline-block w-[20px] h-[2px] bg-white md:bg-[#FF2D78]" />
              Our Story
            </span>
            <h1 className="font-display text-[48px] md:text-[96px] uppercase mt-2 leading-none font-bold text-white md:text-[#080010]">
              About CBC
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="hidden md:block" style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Brand Story — Warm off-white, wider column */}
      <section className="bg-warm-white py-32 px-4" data-testid="brand-story-section">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="w-full md:w-[70%]"
          >
            <h2 className="font-display text-[40px] md:text-[58px] uppercase mb-8 leading-none" style={{ color: '#FF2D78' }}>
              Bar to Bar. Block to Block.
            </h2>
            <div className="space-y-6 font-body text-[#555] text-base leading-relaxed">
              <p>
                Crawl Baby Crawl started in 2023 with one idea and one city. We thought bar crawls should actually be fun. Not a sad pub crawl with a plastic cup and a photocopied map. Something that actually felt like an event worth showing up to.
              </p>
              <p>
                So we built that. We picked the right bars, locked in the right energy, and showed up ready. People felt the difference immediately. They told their friends. Their friends told their friends.
              </p>
              <p>
                We grew fast because we earned it. City after city, crawl after crawl, we kept the standard high and the vibe higher. Now we operate in multiple cities across the country and we are still moving.
              </p>
              <p>
                No tourist traps. No boring nights. Good drinks, good people, and nights worth talking about on Monday morning.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <div style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Values — Hot Pink */}
      <section className="py-32 px-4" style={{ backgroundColor: '#FF2D78' }} data-testid="values-section">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <Eyebrow light>What We Stand For</Eyebrow>
            <h2 className="font-display text-[48px] md:text-[67px] lg:text-[86px] text-white uppercase mt-2 leading-none">
              Built Different
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-[16px] p-8 transition-transform duration-200 ease-out hover:-translate-y-1"
                data-testid={`value-card-${index}`}
              >
                <value.icon className="w-10 h-10 mb-4" style={{ color: value.color }} />
                <h3 className="font-display text-[24px] uppercase mb-2 font-bold" style={{ color: '#080010' }}>{value.title}</h3>
                <p className="font-body text-[#777] text-sm leading-relaxed">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ height: '5px', backgroundColor: '#B8FF2E' }} />

      {/* Closing Quote — Deep Dark */}
      <section className="py-32 px-4 relative overflow-hidden" style={{ backgroundColor: '#080010' }} data-testid="closing-quote-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none" aria-hidden="true">
          <span className="font-display text-[200px] md:text-[400px] lg:text-[500px] leading-none text-white/[0.04]">"</span>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <p className="font-display text-[36px] md:text-[58px] lg:text-[77px] leading-none" style={{ color: '#FF2D78', transform: 'rotate(-2deg)' }}>
              "Life's too short for boring nights."
            </p>
            <div className="mt-12">
              <Link
                to="/crawls"
                className="inline-block font-display text-lg uppercase tracking-wider px-10 py-4 rounded-full transition-all duration-150 hover:scale-[1.02] hover:shadow-lg"
                style={{ backgroundColor: '#FF2D78', color: '#FFFFFF' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E0005A'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF2D78'}
                data-testid="find-a-crawl-cta"
              >
                Find a Crawl
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
